import { Spinner } from '@cimplico/components';
import { useEntities } from '../../queries/use-entities';
import { useGroups } from '../../queries/use-groups';
import { Combobox } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { EntityOptions } from './EntityOptions';
import EmptyStateNew from '../empty-state/EmptyStateNew';
import GroupOptions from './GroupOptions';

type ResultsMenuProps = {
    search: string;
    onNavigate: () => void;
};

function ResultsMenu({ search, onNavigate }: ResultsMenuProps): JSX.Element {
    const groupsQuery = useGroups({ search, filter: '', sort: 'sort[dir]=asc&sort[field]=name', page: 1, limit: 999 });
    const entitiesQuery = useEntities({
        search,
        filter: 'isArchived=false',
        sort: 'sort[dir]=asc&sort[field]=name',
        page: 1,
        limit: 999,
        searchFilters: 'searchFilters=isArchived',
    });

    const isLoading = groupsQuery.isLoading || entitiesQuery.isLoading;

    const groups = groupsQuery.data?.groups.map((g) => ({ ...g, entities: g.entities.filter((ent) => ent.isArchived === false) })) ?? [];
    const entities = entitiesQuery.data?.entities ?? [];

    const totalLength = groups.length + entities.length;

    const groupEntityIds = groups.map((g) => g.entities.map((ent) => ent.id)).flat();
    const filteredEntities = entities.filter((entity) => !groupEntityIds.includes(entity.id));

    return (
        <Combobox.Options
            // static
            className="absolute bg-white dark:bg-gray-800 z-40 max-h-96 overflow-y-auto top-10 border border-gray-200 dark:border-white/[0.07] w-[150%] -translate-x-1/4 rounded-md"
        >
            {isLoading && (
                <div className="absolute right-1/2 bottom-1/2 translate-x-1/2">
                    <Spinner />
                </div>
            )}
            {!isLoading && (
                <>
                    {totalLength === 0 && (
                        <div className="p-8">
                            <EmptyStateNew
                                titleSize="small"
                                icon={<MagnifyingGlassIcon />}
                                helpText="Quickly access groups and entities by running a global search"
                                title="No results found"
                            />
                        </div>
                    )}
                    {groups.length > 0 && (
                        <GroupOptions groups={groups} onNavigate={onNavigate} hasOtherEntities={filteredEntities.length > 0} />
                    )}
                    {filteredEntities.length > 0 && (
                        <EntityOptions entities={filteredEntities} onNavigate={onNavigate} hasGroups={groups.length > 0} />
                    )}
                </>
            )}
        </Combobox.Options>
    );
}

export default ResultsMenu;
